import type { NodeRef, RepertoireLine } from '../api/types';
import { useId } from 'react';
import { findOccurrences } from '../state/navigation';

export interface TranspositionPanelProps {
  node: NodeRef | null;
  lines: RepertoireLine[];
  /** The path currently being viewed, to de-emphasize it in the list. */
  currentPath: { lineId: string; ply: number } | null;
  incomingPathCount: number;
  onNavigate: (lineId: string, ply: number) => void;
}

/**
 * Alternate incoming paths (§6.2). A canonical node may be reached by several
 * move orders; this panel lists every loaded line that reaches it and jumps to
 * that line's occurrence without conflating node identity with path context.
 */
export function TranspositionPanel({
  node,
  lines,
  currentPath,
  incomingPathCount,
  onNavigate,
}: TranspositionPanelProps): JSX.Element {
  const occurrences = node === null ? [] : findOccurrences(node, lines);
  const titleId = useId();

  return (
    <section aria-labelledby={titleId} className="panel">
      <h2 id={titleId}>Transpositions</h2>
      <p className="status-text">
        {node === null
          ? 'No position selected.'
          : `Reached by ${incomingPathCount} incoming path${incomingPathCount === 1 ? '' : 's'}.`}
      </p>
      {occurrences.length > 0 ? (
        <ul className="transposition-list">
          {occurrences.map((occurrence) => {
            const line = lines.find((l) => l.lineId === occurrence.lineId);
            const isCurrent =
              currentPath !== null &&
              currentPath.lineId === occurrence.lineId &&
              currentPath.ply === occurrence.ply;
            return (
              <li key={`${occurrence.lineId}-${occurrence.ply}`}>
                <button
                  type="button"
                  onClick={() => onNavigate(occurrence.lineId, occurrence.ply)}
                  aria-current={isCurrent ? 'location' : undefined}
                >
                  {line?.name ?? 'Unnamed line'} — ply {occurrence.ply}
                  {isCurrent ? ' (current)' : ''}
                  <span className="path-preview">
                    {(line?.movesSan ?? []).slice(0, occurrence.ply).join(' · ')}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="muted">No other incoming paths in loaded lines.</p>
      )}
    </section>
  );
}
